import React, { useState } from "react";
import { useDispatch } from "react-redux";

import StandardButton from "../../../shared/formComponents/StandardButton/StandardButton";
import * as coachContactsActions from "../_redux/contactActions";
import ConfirmationPopup from "../../../shared/ConfirmationPopup/ConfirmationPopup";
import "./invite-contact-to-xray.scss";
import { ConditionalRender } from "../../../shared/ConditionalRender/ConditionalRender";
import { BillingDetailsPopup } from "../../Header/BillingDetailsPopup/BillingDetailsPopup";
import {
  isContactInvited,
  isContactRegistered,
} from "../../../../utils/contactUtils";
import UpgradeConfirmationPopup from "../../../shared/UpgradeConfirmationPopup/UpgradeConfirmationPopup";

const InviteContactToXray = ({
  contact,
  open,
  billingDetails,
  handleCancel,
  handleGetBillingDetails,
  setInviteToXrayToggle,
  handleOK,
  callback,
  demoAccess,
}) => {
  const dispatch = useDispatch();
  const [inviteCode, setInviteCode] = useState(null);
  const [inviteBtnDisabled, setInviteBtnDisabled] = useState(false);
  const [showBillingDetailsPopup, setShowBillingDetailsPopup] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");
  const handleClose = () => {
    setInviteBtnDisabled(false);
    setSelectedPlan("");
    handleGetBillingDetails();
    setInviteCode(null);
    handleCancel();
  };

  const handleOKClose = () => {
    setInviteBtnDisabled(false);
    setInviteCode(null);
    handleOK();
  };

  const isCurrentInvited = isContactInvited(contact?.status);
  const isCurrentRegistered = isContactRegistered(contact?.status);

  const title = isCurrentInvited
    ? "Resend Invite to Art of Scale"
    : inviteCode
    ? "Success!"
    : "Invite to Art of Scale";
  const content = inviteCode ? (
    <>
      An invitation has been sent to{" "}
      <span className="contact-name">
        {contact?.firstName + " " + contact?.lastName}
      </span>{" "}
      with selected plan:{" "}
      <span className="text-capitalize">
        {selectedPlan.toLocaleLowerCase()}
      </span>{" "}
      plan. Please ask them to look out for an email from{" "}
      <span className="contact-email">support@artofscale.io</span>
    </>
  ) : (
    <>
      <p>
        You are about to invite{" "}
        <span className="contact-name">
          {contact?.firstName + " " + contact?.lastName}
        </span>{" "}
        to sign-up on Art of Scale. They will receive an email from
        support@artofscale.io with instructions on how to proceed.
      </p>
      {!isCurrentInvited && (
        <div className="border-top">
          <p className="invite-subtext mt-3 mb-1">
            Please select the plan you'd like them to start with.
          </p>
          <p className="mb-0 pricing-subtext">
            <a
              onClick={() => {
                window.open("https://artofscale.io/pricing/", "_blank");
              }}
              className="invite-link-text"
            >
              Click here{" "}
            </a>{" "}
            to see detailed pricing.
          </p>
          <div className="plans-main mt-3">
            <div
              className="basic-plan-main plans-sub d-flex cursor-pointer"
              onClick={() => {
                setSelectedPlan("BASIC");
              }}
            >
              <div
                className={`custom-radio ${
                  selectedPlan === "BASIC" ? "selected" : "not-selected"
                }`}
              >
                <input
                  type="radio"
                  className="me-4 cursor-pointer"
                  value={selectedPlan === "BASIC" ? true : false}
                  checked={selectedPlan === "BASIC" ? true : false}
                  onChange={(e) => {
                    setSelectedPlan("BASIC");
                  }}
                />
              </div>
              <div className="">
                <p className="plan-title mb-0">Basic Plan</p>
                <p className="plan-subtitle my-1">$15 per user/month</p>
                <p className="plan-description mb-0">
                  Access to all features except Scale This Next X-ray &
                  Scalability Plan.
                </p>
              </div>
            </div>
            <div
              className="pro-plan-main plans-sub d-flex cursor-pointer"
              onClick={() => {
                setSelectedPlan("PRO");
              }}
            >
              <div
                className={`custom-radio ${
                  selectedPlan === "PRO" ? "selected" : "not-selected"
                }`}
              >
                <input
                  type="radio"
                  className="me-4 cursor-pointer"
                  value={selectedPlan === "PRO" ? true : false}
                  checked={selectedPlan === "PRO" ? true : false}
                  onChange={(e) => {
                    setSelectedPlan("PRO");
                  }}
                />
              </div>
              <div className="">
                <p className="plan-title mb-0">Pro Plan</p>
                <p className="plan-subtitle my-1">$1000 / year</p>
                <p className="plan-description mb-0">Access to all features.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );

  const cancelCTA = inviteCode ? null : (
    <StandardButton
      text="Cancel"
      className="btn-sm me-2"
      onClick={handleClose}
    />
  );

  const confirmCTA = inviteCode ? (
    <StandardButton
      text="OK"
      className="btn-sm"
      color="btn-success"
      onClick={handleOK ? handleOKClose : handleClose}
    />
  ) : (
    <StandardButton
      text="Send Invite"
      icon={<span className="material-icons send-icon">send</span>}
      reversed
      className="btn-sm"
      color="btn-success"
      disabled={inviteBtnDisabled || isCurrentRegistered || demoAccess}
      onClick={(e) => {
        if (
          !billingDetails ||
          Object.keys(billingDetails).length === 0 ||
          billingDetails === "" ||
          billingDetails === undefined
        ) {
          setInviteToXrayToggle(false);
          setShowBillingDetailsPopup(true);
        } else {
          inviteContactToXray(e, contact);
        }
      }}
    />
  );

  const inviteContactToXray = async (e, contact) => {
    e.preventDefault();
    try {
      setInviteBtnDisabled(true);
      const userId = contact.userId;
      let icode = await dispatch(
        coachContactsActions.inviteContactToXray(userId, {
          isSponsored: true,
          planType: selectedPlan,
        })
      );
      setInviteCode(icode);
      await coachContactsActions.sendInvitationNotification(contact);
    } catch (err) {
      console.error(err);
    } finally {
      setInviteBtnDisabled(false);
    }
  };
  if (demoAccess && open) {
    return (
      <UpgradeConfirmationPopup
        title={"Invite to X-Ray"}
        content={<>Talk to us to unlock access to invite your first client</>}
        closePopup={handleClose}
        isOpen={open}
      />
    );
  }

  return (
    <React.Fragment>
      <ConfirmationPopup
        title={title}
        content={content}
        cancelAction={cancelCTA}
        confirmAction={confirmCTA}
        isOpen={open}
      />
      <ConditionalRender condition={showBillingDetailsPopup}>
        <BillingDetailsPopup
          callback={() => callback()}
          closePopup={() => setShowBillingDetailsPopup(false)}
          billingDetails={billingDetails}
        />
      </ConditionalRender>
    </React.Fragment>
  );
};

export default InviteContactToXray;
